(() => {
  if (window.RouisInfiniteScroll) {
    window.RouisInfiniteScroll.mount();
    return;
  }

  class RouisInfiniteScroll {
    constructor() {
      this.mount = this.mount.bind(this);
      this.scheduleMount = this.scheduleMount.bind(this);

      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', this.mount, { once: true });
      } else {
        this.mount();
      }

      document.addEventListener('shopify:section:load', this.mount);
    }

    mount() {
      this.disconnect();

      this.container = document.getElementById('ProductGridContainer');
      if (!this.container) return;

      // Shopify's filter and sort controls replace this container's contents.
      // Reconnect the observer after each replacement so the newly filtered grid
      // continues to auto-load its following pages.
      this.containerObserver = new MutationObserver(this.scheduleMount);
      this.containerObserver.observe(this.container, { childList: true });

      this.grid = this.container.querySelector('#product-grid');
      this.control = this.container.querySelector('[data-infinite-scroll]');
      if (!this.grid || !this.control) return;

      this.status = this.control.querySelector('[data-infinite-scroll-status]');
      this.spinner = this.control.querySelector('.loading__spinner');
      this.retryButton = this.control.querySelector('.infinite-scroll__retry');
      this.retryButton?.addEventListener('click', () => this.loadNextPage());

      if (!this.control.dataset.nextUrl) {
        this.finish();
        return;
      }

      if (!('IntersectionObserver' in window)) {
        this.showRetry('Automatic loading is unavailable in this browser.');
        return;
      }

      this.intersectionObserver = new IntersectionObserver(
        (entries) => {
          if (entries.some((entry) => entry.isIntersecting)) this.loadNextPage();
        },
        { rootMargin: '600px 0px' }
      );
      this.intersectionObserver.observe(this.control);
    }

    scheduleMount() {
      if (this.mountRequest) return;
      this.mountRequest = window.requestAnimationFrame(() => {
        this.mountRequest = null;
        this.mount();
      });
    }

    disconnect() {
      this.intersectionObserver?.disconnect();
      this.containerObserver?.disconnect();
      this.abortController?.abort();
      if (this.mountRequest) window.cancelAnimationFrame(this.mountRequest);

      this.intersectionObserver = null;
      this.containerObserver = null;
      this.abortController = null;
      this.mountRequest = null;
      this.loading = false;
    }

    async loadNextPage() {
      if (this.loading || !this.control?.dataset.nextUrl) return;

      const control = this.control;
      const grid = this.grid;
      const nextUrl = control.dataset.nextUrl;
      const controller = new AbortController();

      this.loading = true;
      this.abortController = controller;
      this.setLoading(true);
      this.retryButton.hidden = true;

      try {
        const response = await fetch(nextUrl, {
          signal: controller.signal,
          headers: { 'X-Requested-With': 'XMLHttpRequest' },
        });
        if (!response.ok) throw new Error(`Unable to load collection page (${response.status}).`);

        const html = new DOMParser().parseFromString(await response.text(), 'text/html');
        const nextGrid = html.querySelector('#product-grid');
        const nextControl = html.querySelector('[data-infinite-scroll]');
        const productCards = nextGrid
          ? Array.from(nextGrid.children).filter((element) => element.matches('li.grid__item'))
          : [];

        if (!nextGrid || !nextControl || productCards.length === 0) {
          throw new Error('The next collection page did not contain product cards.');
        }

        // A new filter/sort result can replace the grid while a request is in flight.
        // In that case, discard this stale response rather than mixing two result sets.
        if (this.control !== control || this.grid !== grid || !grid.isConnected) return;

        grid.append(...productCards);
        control.dataset.nextUrl = nextControl.dataset.nextUrl || '';
        this.status.textContent = '';

        if (!control.dataset.nextUrl) {
          this.finish();
        } else {
          this.intersectionObserver.unobserve(control);
          this.intersectionObserver.observe(control);
        }
      } catch (error) {
        if (error.name !== 'AbortError' && this.control === control) {
          this.showRetry('Unable to load more products. Please try again.');
          console.error(error);
        }
      } finally {
        if (this.abortController === controller) this.abortController = null;
        if (this.control === control) this.setLoading(false);
      }
    }

    setLoading(isLoading) {
      this.spinner?.classList.toggle('hidden', !isLoading);
      this.control?.toggleAttribute('aria-busy', isLoading);
      if (isLoading && this.status) {
        this.status.textContent = this.control.dataset.loadingLabel || 'Loading...';
      }
    }

    showRetry(message) {
      this.status.textContent = message;
      this.retryButton.hidden = false;
      this.intersectionObserver?.unobserve(this.control);
    }

    finish() {
      this.intersectionObserver?.disconnect();
      this.control.hidden = true;
    }
  }

  window.RouisInfiniteScroll = new RouisInfiniteScroll();
})();
